import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  OnChanges,
  Output,
  EventEmitter,
} from "@angular/core";
import { WindowRef } from "../../window/WindowRef";
import html2canvas from "html2canvas";

import { KpiService } from "../../kpi/kpi.service";
import { Subscription } from "rxjs";
import { keys, without, cloneDeep, has, find } from "lodash";
import { DeepDiveLightboxComponent } from "../deep-dive-lightbox/deep-dive-lightbox.component";
import { MatDialog } from "@angular/material/dialog";
import { ChartIconButtonComponent } from "../chart-icon-button/chart-icon-button.component";
import { OOPS_MESSAGES } from "../../constants";
import { saveAs, getDownloadTileName } from "../../shared/index";
import { FRECKLE_METRIC_IDS } from "../../constants";

@Component({
  selector: "tile",
  templateUrl: "./tile.component.html",
  styleUrls: ["./tile.component.scss"],
})
export class TileComponent
  extends ChartIconButtonComponent
  implements OnInit, OnDestroy {
  @ViewChild("ViewSelectBtns") viewSelectBtns;

  @Input()
  metadata;

  @Input()
  level;

  @Input()
  entityId;

  @Input()
  studentFirstName;

  @Input()
  role;

  @Input()
  hasAllSchools;

  @Input()
  filterCategory;

  @Input()
  filterValue;

  @Input()
  useRealTimeSchoolProfileRequest: boolean = false;

  @Output() clearFilterEvent = new EventEmitter<any>();

  data;
  loading: boolean;

  categories;
  selectedCategory;
  initialCategory;

  ranks;
  selectedRank;
  initialRank;

  isHovered: boolean;

  partialImagePath: string = "../../assets/images/";

  schoolKPIDataSub: Subscription;
  districtKPIDataSub: Subscription;

  filterSentence: string;
  filteredOverallChartData;

  filterByCurrentlyEnrolledText: string =
    "This selection is showing only currently enrolled students";

  isFreckleMetric: boolean;

  constructor(
    public kpiService: KpiService,
    public winRef: WindowRef,
    public dialog: MatDialog
  ) {
    super(winRef, kpiService, dialog);
  }

  ngOnInit() {
    this.getChartsForTile(this.metadata, this.entityId);
    this.isFreckleMetric =
      this.metadata &&
      this.metadata.KPI &&
      FRECKLE_METRIC_IDS.includes(this.metadata.KPI);
  }

  ngOnDestroy() {
    if (this.schoolKPIDataSub) {
      this.schoolKPIDataSub.unsubscribe();
    } else if (this.districtKPIDataSub) {
      this.districtKPIDataSub.unsubscribe();
    }
  }

  ngOnChanges(change) {
    if (change.filterCategory || change.filterValue) {
      this.handleDistrictProfileFilters();
    }
  }

  handleDistrictProfileFilters() {
    this.filterSentence =
      this.filterCategory && this.filterValue
        ? `Filtered by ${this.filterCategory}: ${this.filterValue}`
        : null;
    if (this.data && this.filterCategory && this.filterValue) {
      this.filteredOverallChartData = this.getFilteredChartData(this.data);
    }
  }

  getChartsForTile(kpi, entityId = null) {
    this.loading = true;
    if (this.level === "school") {
      if (this.useRealTimeSchoolProfileRequest) {
        this.schoolKPIDataSub = this.kpiService
          .getRealtimeSchoolKPIData(entityId, kpi.KPI)
          .subscribe(
            (data) => {
              this.data = data;
              this.loading = false;
              this.setUpRanksAndCategories();
            },
            (error) => {
              this.loading = false;
              this.handleError(error);
            }
          );
      } else {
        this.schoolKPIDataSub = this.kpiService
          .getSchoolKPIData(entityId, kpi.KPI)
          .subscribe(
            (data) => {
              this.data = data;
              this.loading = false;
              this.setUpRanksAndCategories();
            },
            (error) => {
              this.loading = false;
              this.handleError(error);
            }
          );
      }
    } else if (this.level === "district") {
      this.districtKPIDataSub = this.kpiService.getKPIData(kpi.KPI).subscribe(
        (data) => {
          this.data = data;
          this.loading = false;
          this.setUpRanksAndCategories();
          if (this.filterCategory && this.filterValue) {
            this.handleDistrictProfileFilters();
          }
        },
        (error) => {
          this.loading = false;
          this.handleError(error);
        }
      );
    } else {
      // we have a usage tile
      this.loading = false;
      this.data = this.metadata;
    }
  }

  handleError(error) {
    this.kpiService.showError(
      error,
      OOPS_MESSAGES.ACCESS_CONTENT_VIEW,
      OOPS_MESSAGES.RENDER_VIEW
    );
  }

  getChartNames(charts) {
    let chartNames = keys(charts);
    if (chartNames) {
      if (
        (this.level == "district" && this.hasAllSchools) ||
        (this.level == "school" &&
          (this.role == "admin" || this.role == "school_leader"))
      ) {
        chartNames.push("expand");
      }

      if (
        (this.level == "district" || this.level == "school") &&
        (this.role == "admin" ||
          this.role == "school_leader" ||
          this.role == "teacher") &&
        this.data &&
        this.data.domainId == 1005 &&
        this.data.otherReportsUrl
      ) {
        // metric upload metrics with otherReportsUrls are shown the Other Reports button
        // on the district and school tiles if they are an admin, school leader or teacher
        chartNames.push("otherReports");
      }

      if (this.level != "district") {
        // strip 'topBottomRank' from array
        chartNames = without(chartNames, "topBottomRank");
      }
      chartNames = chartNames.reverse();
    }
    return chartNames;
  }

  changeSelectedChart(key) {
    this.resetChartConfigs();
    if (key.value == "otherReports") {
      // open Go To Other Reports modal
      this.kpiService.trackNavButtonClicked("Other Reports").subscribe();
      this.url = this.data.otherReportsUrl;
      this.selectBtn("otherReports");
    } else {
      if (key.value == "expand") {
        this.openLightbox(this.data);
      } else {
        this.data.selectedChart = key.value;
      }
      this.kpiService.trackChartClicked(key.value, this.data.kpiId).subscribe(
        (data) => {},
        (error) => {
          console.error(error);
        }
      );
    }
  }

  resetChartConfigs() {
    this.selectedCategory = this.initialCategory
      ? this.initialCategory.text
      : null;
    this.selectedRank = this.initialRank ? this.initialRank.text : null;
  }

  setUpRanksAndCategories() {
    if (
      this.data.charts.characteristic &&
      this.data.charts.characteristic.chartData
    ) {
      this.categories = Object.keys(
        this.data.charts.characteristic.chartData
      ).sort();
      if (this.categories.indexOf("Primary Ethnicity") > -1) {
        this.selectedCategory = "Primary Ethnicity";
      } else {
        this.selectedCategory = this.categories[0];
      }
      this.initialCategory = {
        id: this.selectedCategory,
        text: this.selectedCategory,
      };
    }

    this.ranks = ["highest 5", "lowest 5"];
    this.selectedRank = this.data.upIsGood === true ? "highest 5" : "lowest 5";
    this.initialRank = { id: this.selectedRank, text: this.selectedRank };
  }

  getRouteForCog(kpiId) {
    return "/metric/settings/editor/" + kpiId;
  }

  openLightbox(chartData) {
    let dialogRef = this.dialog.open(DeepDiveLightboxComponent, {
      width: "90%",
      height: "90%",
      data: {
        lightbox: cloneDeep(chartData),
        periodicity: this.data.periodicity,
        schoolId: this.level === "school" ? this.entityId : null,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      setTimeout(() => {
        this.viewSelectBtns.unHighlightChart("expand");
      });
    });
  }

  captureScreen(id, name) {
    // track click
    this.kpiService
      .trackDownloadTileImageClick(
        id,
        this.data ? this.data.selectedChart : null
      )
      .subscribe(
        (data) => {},
        (error) => {
          console.error(error);
        }
      );
    let data = document.getElementById("contentToConvert-" + id);
    html2canvas(data, {
      scale: 2,
    }).then((canvas) => {
      saveAs(canvas.toDataURL(), getDownloadTileName(name, "mosaic-tile"));
    });
  }

  getFilteredChartData(data) {
    if (
      data &&
      data.charts &&
      data.charts.characteristic &&
      data.charts.characteristic.chartData
    ) {
      let filteredChartDataByCategory =
        data.charts.characteristic.chartData[this.filterCategory];
      if (filteredChartDataByCategory && filteredChartDataByCategory.data) {
        return [
          find(filteredChartDataByCategory.data, ["name", this.filterValue]),
        ];
      }
    }
  }

  resetChartsToOverall() {
    this.data.selectedChart = "overall";
  }
}
